import React from "react";
import { useUnit } from "effector-react";
import { useIdeaCommentsViewModel } from "../../viewmodel/useIdeaCommentsViewModel";
import { $userProfile } from "@/features/user/store";
import Loader from "@/core/components/loader/Loader";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { imageList } from "@/core/utils/imageList";
import { Link } from "react-router-dom";

interface DiscussionTabProps {
  ideaId: string | undefined;
  ideaState: string;
}

export const DiscussionTab: React.FC<DiscussionTabProps> = ({
  ideaId,
  ideaState,
}) => {
  const userProfile = useUnit($userProfile);
  const {
    comments,
    loading,
    newComment,
    setNewComment,
    createLoading,
    replyingTo,
    replyText,
    setReplyText,
    createComment,
    createReply,
    handleReplyClick,
  } = useIdeaCommentsViewModel(ideaId);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatCommentDate = (dateInput: string | number) => {
    // console.log(dateInput, "dateInput");
    const date =
      typeof dateInput === "number"
        ? new Date(dateInput * 1000)
        : new Date(Number(dateInput) * 1000);

    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60 * 60),
    );

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return "Yesterday";
    return (
      date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) +
      " " +
      date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    );
  };

  const mainComments = comments.filter(
    (comment) => !comment.parent_id || comment.parent_id === "0",
  );

  // console.log(mainComments, "main comments");
  const replies = comments.filter(
    (comment) => comment.parent_id && comment.parent_id !== "0",
  );

  return (
    <div className="discussion-tab">
      <div className="comments-section">
        {loading ? (
          <div className="comments-loading">
            <Loader />
          </div>
        ) : (
          <div className="comments-list">
            {comments.length > 0 ? (
              mainComments.map((comment) => {
                const commentReplies = replies.filter(
                  (reply) => reply.parent_id === comment.id,
                );
                // console.log(commentReplies, "comment replies");

                return (
                  <div key={comment.id} className="comment-thread">
                    <div className="comment-item">
                      <div className="comment-avatar">
                        <Link to={`/users/${comment.user_id}`}>
                          <div className="avatar placeholderImage">
                            <BgImage
                              img={
                                comment.user_profile_image
                                  ? comment.user_profile_image
                                  : imageList.noimage
                              }
                            />
                          </div>
                        </Link>
                      </div>
                      <div className="comment-content">
                        <Link to={`/users/${comment.user_id}`}>
                          <div className="comment-header">
                            <span className="comment-author">
                              {comment.user_profile_name}
                            </span>
                            <span className="comment-time">
                              {formatCommentDate(comment.created_time)}
                            </span>
                          </div>
                        </Link>

                        <div
                          className="comment-text"
                          dangerouslySetInnerHTML={{ __html: comment.body }}
                        />
                        {ideaState.toLowerCase() === "approved" && (
                          <button
                            className="reply-btn"
                            onClick={() => handleReplyClick(comment.id)}
                          >
                            Reply
                          </button>
                        )}
                      </div>
                    </div>

                    {commentReplies.map((reply) => (
                      <div
                        key={reply.id}
                        className="comment-item reply-comment"
                      >
                        <div className="comment-avatar">
                          <div className="avatar placeholderImage">
                            <Link to={`/users/${reply.user_id}`}>
                              <BgImage
                                img={
                                  reply.user_profile_image
                                    ? reply.user_profile_image
                                    : imageList.noimage
                                }
                              />
                            </Link>
                          </div>
                        </div>
                        <div className="comment-content">
                          <div className="comment-header">
                            <Link to={`/users/${reply.user_id}`}>
                              <span className="comment-author">
                                {reply.user_profile_name}
                              </span>
                            </Link>
                            <span className="comment-time">
                              {formatCommentDate(reply.created_time)}
                            </span>
                          </div>
                          <div
                            className="comment-text"
                            dangerouslySetInnerHTML={{ __html: reply.body }}
                          />
                        </div>
                      </div>
                    ))}

                    {ideaState.toLowerCase() === "approved" &&
                      replyingTo === comment.id && (
                        <div className="reply-form">
                          <div className="comment-input">
                            <textarea
                              placeholder="Write your reply here..."
                              rows={2}
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                            />
                            <div className="comment-actions">
                              <button
                                className="btn-secondary"
                                onClick={() => handleReplyClick(comment.id)}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn-primary"
                                disabled={createLoading || !replyText.trim()}
                                onClick={() => createReply(comment.id)}
                              >
                                {createLoading ? "Posting..." : "Post Reply"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                );
              })
            ) : (
              <div className="no-comments">
                {ideaState.toLowerCase() === "approved" && (
                  <p>No comments yet. Be the first to comment!</p>
                )}
              </div>
            )}
          </div>
        )}

        {ideaState.toLowerCase() === "approved" && (
          <div className="comment-form">
            <div className="comment-avatar">
              <div className="avatar placeholderImage">
                <BgImage
                  img={
                    userProfile &&
                    typeof userProfile.user_data.user_picture === "object" &&
                    userProfile?.user_data?.user_picture?.[0]?.url
                      ? userProfile.user_data.user_picture?.[0]?.url
                      : imageList.noimage
                  }
                />
              </div>
            </div>
            <div className="comment-input">
              <textarea
                placeholder="Write your comment here..."
                rows={3}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
              <div className="comment-actions">
                <button
                  className="btn-primary"
                  disabled={createLoading || !newComment.trim()}
                  onClick={createComment}
                >
                  {createLoading ? "Posting..." : "Post Comment"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
